import React, { useState, useEffect } from 'react'
import AppLayout from '../components/AppLayout'
import { useAuth } from '../contexts/AuthContext'
import { supabase, ugandaDate, ugandaMonth, ugandaDatetime } from '../lib/supabase'

const CROPS = ['Lettuce', 'Strawberries', 'Mixed Leafy Greens', 'Herbs', 'Swiss Chard', 'Other']

const CROP_VARIETIES = {
  'Lettuce': ['Butterhead', 'Lollo Rosso', 'Lollo Bionda', 'Nutrired', 'Roma', 'Oakleaf', 'Batavian', 'Cos / Romaine', 'Other / Not specified'],
  'Strawberries': ['Albion', 'Chandler', 'Festival', 'Sweet Charlie', 'Other / Not specified'],
  'Mixed Leafy Greens': ['Rocket / Arugula', 'Spinach', 'Mizuna', 'Mustard Greens', 'Other / Not specified'],
  'Herbs': ['Basil', 'Coriander', 'Parsley', 'Mint', 'Chives', 'Dill', 'Other / Not specified'],
  'Swiss Chard': ['Rainbow Chard', 'Fordhook Giant', 'Other / Not specified'],
  'Other': ['Other / Not specified'],
}

const SYSTEMS_ZONES = [
  { system: 'A-Frame 5.6m (NFT)', zones: ['Full frame', 'Left side (rows 1-14)', 'Right side (rows 15-28)', 'Top channels', 'Bottom channels', 'Custom zone'] },
  { system: 'A-Frame 12m (PVC Pipe)', zones: ['Full frame', 'Section A (rows 1-14)', 'Section B (rows 15-28)', 'Section C (rows 29-42)', 'Section D (rows 43-56)', 'Custom zone'] },
  { system: 'Straw Trough System', zones: ['Full system', 'Trough 1', 'Trough 2', 'Trough 3', 'Trough 4', 'Custom zone'] },
  { system: 'Other', zones: ['Full system', 'Custom zone'] },
]
const SYSTEMS = ['A-Frame 5.6m (NFT)', 'A-Frame 12m (PVC Pipe)', 'Straw Trough System', 'Other']
const STAGES = ['Seedling / Nursery', 'Transplanted', 'Growing', 'Fruiting', 'Ready to Harvest', 'Harvesting', 'Complete']
const STAGE_COLORS = {
  'Seedling / Nursery': '#2563eb', 'Transplanted': '#9333ea',
  'Growing': '#16a34a', 'Fruiting': '#d97706',
  'Ready to Harvest': '#15803d', 'Harvesting': '#b45309', 'Complete': '#6b7280',
}

const CROP_CODES = {
  'Lettuce': 'LET', 'Strawberries': 'STR', 'Mixed Leafy Greens': 'MLG',
  'Herbs': 'HRB', 'Swiss Chard': 'SWC', 'Other': 'OTH'
}

export default function CropCycles() {
  const { user, isAdminOrManager } = useAuth()
  const [cycles, setCycles] = useState([])
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState('list')
  const [selected, setSelected] = useState(null)
  const [detail, setDetail] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState(null)

  const emptyForm = () => ({
    cycle_name: '', crop: 'Lettuce', variety: 'Butterhead',
    system: 'A-Frame 5.6m (NFT)', zone: 'Full frame',
    plant_count: '', start_date: ugandaDate(),
    transplant_date: ugandaDate(),
    expected_harvest_date: '', current_stage: 'Transplanted',
    seedling_supplier: 'Urban Greens Ltd', seedling_qty: '', seedling_cost: '',
    notes: '',
  })
  const [form, setForm] = useState(emptyForm())

  useEffect(() => { loadCycles() }, [])

  const loadCycles = async () => {
    setLoading(true)
    const { data } = await supabase.from('crop_cycles').select('*').order('start_date', { ascending: false })
    setCycles(data || [])
    setLoading(false)
  }

  // Auto-generate cycle name: LET-001-APR26
  const generateCycleName = async (crop) => {
    const code = CROP_CODES[crop] || 'OTH'
    const { data } = await supabase.from('crop_cycles').select('cycle_name').like('cycle_name', `${code}-%`)
    const count = (data || []).length + 1
    const seq = String(count).padStart(3, '0')
    const now = new Date()
    const mon = now.toLocaleString('en', { month: 'short' }).toUpperCase()
    const yr = String(now.getFullYear()).slice(2)
    return `${code}-${seq}-${mon}${yr}`
  }

  const handleNew = async () => {
    const f = emptyForm()
    f.cycle_name = await generateCycleName(f.crop)
    setSelected(null); setForm(f); setTab('form')
  }

  const handleCropChange = async (crop) => {
    const name = await generateCycleName(crop)
    const firstVariety = CROP_VARIETIES[crop]?.[0] || 'Other / Not specified'
    setForm(f => ({ ...f, crop, variety: firstVariety, cycle_name: name }))
  }

  const handleSystemChange = (system) => {
    const firstZone = SYSTEMS_ZONES.find(s => s.system === system)?.zones[0] || 'Full system'
    setForm(f => ({ ...f, system, zone: firstZone }))
  }

  const loadCycleDetail = async (cycle) => {
    setDetailLoading(true)
    const start = cycle.start_date
    const end = ugandaDate()

    // Harvests tagged to this cycle directly
    const { data: harvests } = await supabase
      .from('harvest_logs')
      .select('*, profiles(full_name), crops(name)')
      .eq('cycle_id', cycle.id)
      .order('harvest_date')

    // Farm logs tagged to this cycle
    const { data: farmLogs } = await supabase
      .from('farm_logs')
      .select('log_date, chemical_rows, profiles(full_name)')
      .eq('cycle_id', cycle.id)
      .order('log_date')

    const chemicals = []
    ;(farmLogs || []).forEach(log => {
      ;(log.chemical_rows || []).forEach(row => {
        if (row.product) chemicals.push({
          date: log.log_date,
          logged_by: log.profiles?.full_name || '—',
          product: row.product,
          amount: row.amount,
          reason: row.reason,
        })
      })
    })

    // Invoices in date range
    const { data: invoices } = await supabase
      .from('invoices')
      .select('invoice_number, invoice_date, customer_name, amount, status, items')
      .gte('invoice_date', start)
      .lte('invoice_date', end)
      .order('invoice_date')

    const cropInvoices = (invoices || []).filter(inv =>
      (inv.items || []).some(item =>
        item.product?.toLowerCase().includes(cycle.crop.toLowerCase()) ||
        item.description?.toLowerCase().includes(cycle.crop.toLowerCase())
      )
    )

    setDetail({ cycle, harvests: harvests || [], chemicals, invoices: cropInvoices })
    setDetailLoading(false)
  }

  const handleOpen = async (cycle) => {
    setSelected(cycle)
    setForm({ ...emptyForm(), ...cycle })
    setTab('detail')
    await loadCycleDetail(cycle)
  }

  const handleSave = async () => {
    if (!form.cycle_name || !form.crop) { setMsg({ type: 'error', text: 'Cycle name and crop required.' }); return }
    setSaving(true)

    // Auto-assign income account code for new cycles
    let incomeAccount = form.income_account
    if (!selected && !incomeAccount) {
      // Find highest existing 4xxx code in COA
      const { data: incomeCodes } = await supabase
        .from('chart_of_accounts')
        .select('code')
        .gte('code', '4100')
        .lte('code', '4999')
        .order('code', { ascending: false })
        .limit(1)

      const highestCode = incomeCodes?.[0]?.code ? parseInt(incomeCodes[0].code) : 4105
      incomeAccount = String(highestCode + 1)

      // Register in chart_of_accounts
      const accountName = `${form.crop}${form.variety ? ' — ' + form.variety : ''} Sales`
      await supabase.from('chart_of_accounts').upsert({
        code: incomeAccount,
        name: accountName,
        category: 'Income',
        subcategory: 'Revenue',
        normal_balance: 'Credit',
        is_active: true,
      }, { onConflict: 'code', ignoreDuplicates: true })
    }

    const payload = { ...form, income_account: incomeAccount, user_id: user.id, updated_at: new Date().toISOString() }
    let error
    if (selected) {
      ;({ error } = await supabase.from('crop_cycles').update(payload).eq('id', selected.id))
    } else {
      ;({ error } = await supabase.from('crop_cycles').insert({ ...payload, created_at: new Date().toISOString() }))
    }
    if (error) { setMsg({ type: 'error', text: error.message }); setSaving(false); return }
    setMsg({ type: 'success', text: `Cycle ${form.cycle_name} saved. Income account ${incomeAccount} assigned.` })
    setSaving(false); loadCycles()
    if (selected) { const updated = { ...selected, ...form }; setSelected(updated); await loadCycleDetail(updated) }
    else setTab('list')
    setTimeout(() => setMsg(null), 3000)
  }

  const getStageColor = (stage) => STAGE_COLORS[stage] || '#6b7280'

  const CycleCard = ({ cycle }) => {
    const days = cycle.transplant_date
      ? Math.floor((new Date() - new Date(cycle.transplant_date)) / 86400000) : null
    return (
      <div className="card" style={{ cursor: 'pointer', borderLeft: `4px solid ${getStageColor(cycle.current_stage)}` }}
        onClick={() => handleOpen(cycle)}
        onMouseEnter={e => e.currentTarget.style.boxShadow = 'var(--shadow-md)'}
        onMouseLeave={e => e.currentTarget.style.boxShadow = 'var(--shadow)'}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
          <div>
            <div style={{ fontFamily: "'DM Mono',monospace", fontSize: '1rem', fontWeight: 700, color: 'var(--green-deep)', letterSpacing: '0.05em' }}>{cycle.cycle_name}</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>
              {cycle.crop}{cycle.variety ? ` — ${cycle.variety}` : ''} · {cycle.system}{cycle.zone && cycle.zone !== 'Full frame' && cycle.zone !== 'Full system' ? ` · ${cycle.zone}` : ''}
                {cycle.income_account && (
                  <span style={{ marginLeft: 8, fontFamily: "'DM Mono',monospace", fontSize: '0.65rem', background: 'var(--green-pale)', color: 'var(--green-deep)', borderRadius: 4, padding: '1px 5px' }}>
                    {cycle.income_account}
                  </span>
                )}
            </div>
          </div>
          <span style={{ background: getStageColor(cycle.current_stage), color: 'white', padding: '3px 12px', borderRadius: 20, fontSize: '0.65rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
            {cycle.current_stage}
          </span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8 }}>
          {[
            ['🌱 Plants', cycle.plant_count || '—'],
            ['📅 Transplanted', cycle.transplant_date ? new Date(cycle.transplant_date).toLocaleDateString('en-UG') : '—'],
            ['⏱️ Days in ground', days !== null && days >= 0 ? `${days}d` : '—'],
            ['🎯 Target harvest', cycle.expected_harvest_date ? new Date(cycle.expected_harvest_date).toLocaleDateString('en-UG') : '—'],
            ['💰 Seedling cost', cycle.seedling_cost ? `UGX ${parseFloat(cycle.seedling_cost).toLocaleString()}` : '—'],
            ['🏷️ Supplier', cycle.seedling_supplier || '—'],
          ].map(([label, val]) => (
            <div key={label} style={{ background: 'var(--cream)', borderRadius: 6, padding: '6px 10px' }}>
              <div style={{ fontSize: '0.6rem', color: 'var(--text-muted)', marginBottom: 2 }}>{label}</div>
              <div style={{ fontWeight: 700, fontSize: '0.8rem' }}>{val}</div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <AppLayout title="Crop Cycles">
      <div className="section-header">
        <div className="section-icon">🌿</div>
        <div className="section-info">
          <h2>Crop Cycles</h2>
          <p>Start a cycle on transplant day. Farm Log and Harvest Log tag to it automatically. No double entry.</p>
        </div>
        {tab === 'list' && isAdminOrManager && (
          <button className="btn btn-primary" style={{ marginLeft: 'auto' }} onClick={handleNew}>🌱 Start New Cycle</button>
        )}
        {tab !== 'list' && (
          <button className="btn btn-ghost" style={{ marginLeft: 'auto' }} onClick={() => { setTab('list'); setDetail(null) }}>← All Cycles</button>
        )}
      </div>

      {msg && <div className={`alert ${msg.type === 'success' ? 'alert-green' : 'alert-red'}`}>{msg.text}</div>}

      {/* LIST */}
      {tab === 'list' && (
        loading
          ? <div style={{ textAlign: 'center', padding: 48 }}><span className="spinner dark" /></div>
          : cycles.length === 0
            ? (
              <div className="empty-state">
                <div className="empty-icon">🌿</div>
                <h3>No crop cycles yet</h3>
                <p>On the day you transplant seedlings, click "Start New Cycle". Everything else — inputs, harvests, sales — links automatically.</p>
                <button className="btn btn-primary" onClick={handleNew}>🌱 Start First Cycle</button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div className="alert alert-green" style={{ fontSize: '0.78rem' }}>
                  💡 <strong>How it works:</strong> When transplanting → Start a cycle here. When logging daily → Farm Log asks which cycle. When harvesting → Harvest Log asks which cycle. Everything links automatically.
                </div>
                {cycles.filter(c => c.current_stage !== 'Complete').map(c => <CycleCard key={c.id} cycle={c} />)}
                {cycles.filter(c => c.current_stage === 'Complete').length > 0 && <>
                  <div style={{ fontSize: '0.72rem', fontFamily: "'DM Mono',monospace", textTransform: 'uppercase', color: 'var(--text-muted)', padding: '8px 0' }}>Completed Cycles</div>
                  {cycles.filter(c => c.current_stage === 'Complete').map(c => <CycleCard key={c.id} cycle={c} />)}
                </>}
              </div>
            )
      )}

      {/* NEW CYCLE FORM */}
      {tab === 'form' && (
        <div className="card">
          <div className="card-title">🌱 Start New Crop Cycle</div>
          <div className="alert alert-green" style={{ marginBottom: 16, fontSize: '0.78rem' }}>
            ✅ Do this on the day you transplant seedlings into the system. The cycle code is auto-generated — you'll select it in the Farm Log and Harvest Log going forward.
          </div>

          {/* Auto-generated name */}
          <div style={{ background: 'var(--green-pale)', border: '2px solid var(--green-mid)', borderRadius: 10, padding: '14px 18px', marginBottom: 18, display: 'flex', alignItems: 'center', gap: 16 }}>
            <div>
              <div style={{ fontSize: '0.7rem', fontFamily: "'DM Mono',monospace", textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 4 }}>Auto-generated cycle code</div>
              <div style={{ fontFamily: "'DM Mono',monospace", fontSize: '1.4rem', fontWeight: 700, color: 'var(--green-deep)', letterSpacing: '0.1em' }}>{form.cycle_name}</div>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 2 }}>This code appears in Farm Log and Harvest Log dropdowns</div>
            </div>
          </div>

          <div className="grid-2" style={{ marginBottom: 14 }}>
            <div className="form-group"><label className="form-label">Crop *</label>
              <select className="form-select" value={form.crop} onChange={e => handleCropChange(e.target.value)}>
                {CROPS.map(c => <option key={c}>{c}</option>)}
              </select></div>
            <div className="form-group"><label className="form-label">Variety *</label>
              <select className="form-select" value={form.variety} onChange={e => setForm(f => ({ ...f, variety: e.target.value }))}>
                {(CROP_VARIETIES[form.crop] || ['Other / Not specified']).map(v => <option key={v}>{v}</option>)}
              </select></div>
            <div className="form-group"><label className="form-label">Hydroponic System</label>
              <select className="form-select" value={form.system} onChange={e => handleSystemChange(e.target.value)}>
                {SYSTEMS_ZONES.map(s => <option key={s.system}>{s.system}</option>)}
              </select></div>
            <div className="form-group"><label className="form-label">Zone on System</label>
              <select className="form-select" value={form.zone} onChange={e => setForm(f => ({ ...f, zone: e.target.value }))}>
                {(SYSTEMS_ZONES.find(s => s.system === form.system)?.zones || ['Full system', 'Custom zone']).map(z => <option key={z}>{z}</option>)}
              </select>
              {form.zone === 'Custom zone' && (
                <input className="form-input" style={{ marginTop: 6 }} placeholder="Describe the zone e.g. Rows 1-10" value={form.zone_custom || ''} onChange={e => setForm(f => ({ ...f, zone_custom: e.target.value }))} />
              )}
            </div>
            <div className="form-group"><label className="form-label">Number of Plants Transplanted</label>
              <input className="form-input" type="number" value={form.plant_count} onChange={e => setForm(f => ({ ...f, plant_count: e.target.value }))} placeholder="e.g. 941" /></div>
            <div className="form-group"><label className="form-label">Transplant Date (today)</label>
              <input className="form-input" type="date" value={form.transplant_date} onChange={e => setForm(f => ({ ...f, transplant_date: e.target.value, start_date: e.target.value }))} /></div>
            <div className="form-group"><label className="form-label">Expected Harvest Date</label>
              <input className="form-input" type="date" value={form.expected_harvest_date} onChange={e => setForm(f => ({ ...f, expected_harvest_date: e.target.value }))} /></div>
            <div className="form-group"><label className="form-label">Current Stage</label>
              <select className="form-select" value={form.current_stage} onChange={e => setForm(f => ({ ...f, current_stage: e.target.value }))}>
                {STAGES.map(s => <option key={s}>{s}</option>)}
              </select></div>
          </div>

          <div style={{ fontWeight: 700, fontSize: '0.82rem', color: 'var(--green-deep)', marginBottom: 10 }}>🛒 Seedling Details</div>
          <div className="grid-2" style={{ marginBottom: 14 }}>
            <div className="form-group"><label className="form-label">Seedling Supplier</label>
              <input className="form-input" value={form.seedling_supplier} onChange={e => setForm(f => ({ ...f, seedling_supplier: e.target.value }))} /></div>
            <div className="form-group"><label className="form-label">Seedlings Purchased (heads)</label>
              <input className="form-input" type="number" value={form.seedling_qty} onChange={e => setForm(f => ({ ...f, seedling_qty: e.target.value }))} /></div>
            <div className="form-group"><label className="form-label">Total Seedling Cost (UGX)</label>
              <input className="form-input" type="number" value={form.seedling_cost} onChange={e => setForm(f => ({ ...f, seedling_cost: e.target.value }))} /></div>
          </div>

          <div className="form-group" style={{ marginBottom: 16 }}>
            <label className="form-label">Notes</label>
            <textarea className="form-textarea" style={{ minHeight: 60 }} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Variety, batch, special conditions..." />
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-primary btn-lg" onClick={handleSave} disabled={saving}>{saving ? <><span className="spinner" />Saving...</> : '🌱 Start Cycle'}</button>
            <button className="btn btn-ghost" onClick={() => setTab('list')}>Cancel</button>
          </div>
        </div>
      )}

      {/* CYCLE DETAIL */}
      {tab === 'detail' && selected && (
        <div>
          <div className="card" style={{ borderLeft: `4px solid ${getStageColor(form.current_stage)}`, marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
              <div>
                <div style={{ fontFamily: "'DM Mono',monospace", fontSize: '1.3rem', fontWeight: 700, color: 'var(--green-deep)', letterSpacing: '0.08em' }}>{form.cycle_name}</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                  {form.crop}{form.variety ? ` — ${form.variety}` : ''} · {form.system}{form.zone && form.zone !== 'Full frame' && form.zone !== 'Full system' ? ` · ${form.zone}` : ''} · {form.plant_count} plants · {form.seedling_supplier}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <select className="form-select" style={{ width: 180 }} value={form.current_stage} onChange={e => setForm(f => ({ ...f, current_stage: e.target.value }))}>
                  {STAGES.map(s => <option key={s}>{s}</option>)}
                </select>
                <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : '💾 Save'}</button>
                <button className="btn btn-ghost btn-sm" onClick={() => setTab('form')}>✏️ Edit</button>
              </div>
            </div>

            {/* Timeline */}
            <div style={{ display: 'flex', marginBottom: 14, overflowX: 'auto' }}>
              {[
                { label: 'Transplanted', date: form.transplant_date, icon: '🌱', done: !!form.transplant_date },
                { label: 'Target harvest', date: form.expected_harvest_date, icon: '🎯', done: false },
                { label: 'First harvest', date: detail?.harvests?.[0]?.harvest_date, icon: '🌾', done: !!detail?.harvests?.[0] },
                { label: 'Complete', date: form.current_stage === 'Complete' ? form.updated_at?.split('T')[0] : null, icon: '✅', done: form.current_stage === 'Complete' },
              ].map(({ label, date, icon, done }, i) => (
                <div key={i} style={{ flex: 1, textAlign: 'center', position: 'relative', minWidth: 80, padding: '0 4px' }}>
                  <div style={{ fontSize: '1.3rem', opacity: done ? 1 : 0.35 }}>{icon}</div>
                  <div style={{ fontSize: '0.62rem', fontWeight: 700, color: done ? 'var(--green-deep)' : 'var(--text-muted)', marginTop: 2 }}>{label}</div>
                  <div style={{ fontSize: '0.6rem', color: 'var(--text-muted)' }}>{date ? new Date(date).toLocaleDateString('en-UG') : '—'}</div>
                  {i < 3 && <div style={{ position: 'absolute', top: 10, left: '55%', right: 0, height: 2, background: done ? 'var(--green-mid)' : 'var(--border)' }} />}
                </div>
              ))}
            </div>

            {/* KPIs */}
            {(() => {
              const totalHarv = (detail?.harvests || []).reduce((s, h) => s + (parseFloat(h.total_kg) || 0), 0)
              const totalRev = (detail?.invoices || []).reduce((s, i) => s + (parseFloat(i.amount) || 0), 0)
              const days = form.transplant_date ? Math.floor((new Date() - new Date(form.transplant_date)) / 86400000) : null
              return (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10 }}>
                  {[
                    { label: 'Days in ground', value: days !== null && days >= 0 ? `${days}d` : '—', color: 'var(--blue)' },
                    { label: 'Total harvested', value: totalHarv > 0 ? `${totalHarv.toFixed(1)}kg` : 'Not yet', color: 'var(--green-mid)' },
                    { label: 'Inputs logged', value: `${detail?.chemicals?.length || 0} entries`, color: 'var(--amber)' },
                    { label: 'Revenue', value: totalRev > 0 ? `UGX ${totalRev.toLocaleString()}` : '—', color: 'var(--green-deep)' },
                  ].map(({ label, value, color }) => (
                    <div key={label} style={{ background: 'var(--cream)', borderRadius: 8, padding: '10px 12px', textAlign: 'center' }}>
                      <div style={{ fontSize: '0.6rem', color: 'var(--text-muted)', marginBottom: 4, textTransform: 'uppercase', fontFamily: "'DM Mono',monospace" }}>{label}</div>
                      <div style={{ fontWeight: 700, color, fontFamily: "'Cormorant Garamond',serif", fontSize: '1rem' }}>{value}</div>
                    </div>
                  ))}
                </div>
              )
            })()}
          </div>

          {detailLoading
            ? <div style={{ textAlign: 'center', padding: 48 }}><span className="spinner dark" /><div style={{ marginTop: 12, color: 'var(--text-muted)', fontSize: '0.82rem' }}>Loading from your logs...</div></div>
            : (
              <>
                {/* INPUTS */}
                <div className="card" style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <div className="card-title">🧪 Inputs Applied</div>
                    <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>From Farm Log entries tagged to {form.cycle_name}</span>
                  </div>
                  {detail?.chemicals?.length === 0
                    ? <div className="alert alert-amber" style={{ fontSize: '0.78rem' }}>No inputs yet. When you record chemicals or nutrients in the Farm Log, select <strong>{form.cycle_name}</strong> from the cycle dropdown — they'll appear here automatically.</div>
                    : (
                      <div className="table-wrap">
                        <table>
                          <thead><tr><th>Date</th><th>Product</th><th>Amount</th><th>Reason</th><th>Logged By</th></tr></thead>
                          <tbody>
                            {detail.chemicals.map((c, i) => (
                              <tr key={i}>
                                <td style={{ fontSize: '0.75rem' }}>{new Date(c.date).toLocaleDateString('en-UG')}</td>
                                <td style={{ fontWeight: 600 }}>{c.product}</td>
                                <td style={{ fontFamily: "'DM Mono',monospace", fontSize: '0.78rem' }}>{c.amount}</td>
                                <td style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{c.reason || '—'}</td>
                                <td style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{c.logged_by}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )
                  }
                  <div style={{ marginTop: 8, fontSize: '0.72rem', color: 'var(--text-muted)' }}>To add → <strong>Farm Log → Chemicals / Nutrients section → select cycle {form.cycle_name}</strong></div>
                </div>

                {/* HARVESTS */}
                <div className="card" style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <div className="card-title">🌾 Harvests</div>
                    <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>From Harvest Log tagged to {form.cycle_name}</span>
                  </div>
                  {detail?.harvests?.length === 0
                    ? <div className="alert alert-amber" style={{ fontSize: '0.78rem' }}>No harvests yet. When harvesting, go to <strong>Harvest Log</strong> and select <strong>{form.cycle_name}</strong> — it will appear here and auto-enter Stores.</div>
                    : (
                      <div className="table-wrap">
                        <table>
                          <thead><tr><th>Date</th><th>Grade A</th><th>Grade B</th><th>Reject</th><th>Total</th><th>By</th></tr></thead>
                          <tbody>
                            {detail.harvests.map((h, i) => (
                              <tr key={i}>
                                <td style={{ fontSize: '0.75rem', fontWeight: 600 }}>{new Date(h.harvest_date).toLocaleDateString('en-UG')}</td>
                                <td style={{ color: 'var(--green-mid)', fontWeight: 600 }}>{h.grade_a_kg}kg</td>
                                <td style={{ color: 'var(--amber)' }}>{h.grade_b_kg}kg</td>
                                <td style={{ color: 'var(--red)' }}>{h.reject_kg}kg</td>
                                <td style={{ fontWeight: 700 }}>{parseFloat(h.total_kg || 0).toFixed(2)}kg</td>
                                <td style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{h.profiles?.full_name || '—'}</td>
                              </tr>
                            ))}
                            <tr style={{ background: 'var(--green-pale)', fontWeight: 700 }}>
                              <td>TOTAL</td>
                              <td style={{ color: 'var(--green-mid)' }}>{detail.harvests.reduce((s, h) => s + (parseFloat(h.grade_a_kg) || 0), 0).toFixed(2)}kg</td>
                              <td style={{ color: 'var(--amber)' }}>{detail.harvests.reduce((s, h) => s + (parseFloat(h.grade_b_kg) || 0), 0).toFixed(2)}kg</td>
                              <td style={{ color: 'var(--red)' }}>{detail.harvests.reduce((s, h) => s + (parseFloat(h.reject_kg) || 0), 0).toFixed(2)}kg</td>
                              <td>{detail.harvests.reduce((s, h) => s + (parseFloat(h.total_kg) || 0), 0).toFixed(2)}kg</td>
                              <td></td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    )
                  }
                </div>

                {/* SALES */}
                <div className="card" style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <div className="card-title">💰 Sales & Revenue</div>
                    <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>From Invoices in cycle period</span>
                  </div>
                  {detail?.invoices?.length === 0
                    ? <div style={{ color: 'var(--text-muted)', fontSize: '0.82rem', padding: '8px 0' }}>No invoices linked yet for this crop in this period.</div>
                    : (
                      <div className="table-wrap">
                        <table>
                          <thead><tr><th>Date</th><th>Invoice</th><th>Customer</th><th>Amount</th><th>Status</th></tr></thead>
                          <tbody>
                            {detail.invoices.map((inv, i) => (
                              <tr key={i}>
                                <td style={{ fontSize: '0.75rem' }}>{new Date(inv.invoice_date).toLocaleDateString('en-UG')}</td>
                                <td style={{ fontFamily: "'DM Mono',monospace", fontSize: '0.78rem' }}>{inv.invoice_number}</td>
                                <td style={{ fontWeight: 600 }}>{inv.customer_name}</td>
                                <td style={{ fontWeight: 700, fontFamily: "'DM Mono',monospace" }}>UGX {parseFloat(inv.amount || 0).toLocaleString()}</td>
                                <td><span className={`badge ${inv.status === 'Paid' ? 'badge-green' : inv.status === 'Overdue' ? 'badge-red' : 'badge-amber'}`}>{inv.status}</span></td>
                              </tr>
                            ))}
                            <tr style={{ background: 'var(--green-pale)', fontWeight: 700 }}>
                              <td colSpan={3}>Total Revenue</td>
                              <td style={{ fontFamily: "'DM Mono',monospace" }}>UGX {detail.invoices.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0).toLocaleString()}</td>
                              <td></td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    )
                  }
                </div>

                {/* NOTES */}
                <div className="card">
                  <div className="card-title">📝 Cycle Notes</div>
                  <textarea className="form-textarea" style={{ minHeight: 80 }} value={form.notes}
                    onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                    placeholder="Observations, issues, lessons learned..." />
                  <button className="btn btn-primary btn-sm" style={{ marginTop: 10 }} onClick={handleSave} disabled={saving}>
                    {saving ? 'Saving...' : '💾 Save Notes'}
                  </button>
                </div>
              </>
            )
          }
        </div>
      )}
    </AppLayout>
  )
}
